import { useMemo, useState } from 'react';
import { Gantt as SvarGantt, Willow, WillowDark, type IApi, type ITask } from '@svar-ui/react-gantt';
import '@svar-ui/react-gantt/all.css';
import type { Project, Task } from '../api.ts';
import { useSave, useTaskDrawer } from '../data.ts';
import { basisLabel, recentlyByAgent } from '../util.ts';

const zooms = {
  week: { cellWidth: 64, scales: [{ unit: 'month', step: 1, format: '%F %Y' }, { unit: 'week', step: 1, format: '%M %j' }] },
  month: { cellWidth: 96, scales: [{ unit: 'year', step: 1, format: '%Y' }, { unit: 'month', step: 1, format: '%F' }] },
  quarter: { cellWidth: 150, scales: [{ unit: 'year', step: 1, format: '%Y' }, { unit: 'quarter', step: 1, format: 'Q%q' }] },
} as const;
type Zoom = keyof typeof zooms;
const toDate = (iso: string) => new Date(`${iso}T00:00:00`);
const toIso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const addDays = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);

// The plan as a timeline. Only dated work is drawn; bars Gemini moved recently carry a badge and the reason.
export function Gantt({ tasks, projects, height = 460, compact = false, emptyAction }: { tasks: Task[]; projects: Project[]; height?: number; compact?: boolean; emptyAction?: React.ReactNode }) {
  const { open } = useTaskDrawer();
  const save = useSave();
  const [zoom, setZoom] = useState<Zoom>('week');
  const dark = document.documentElement.dataset.theme === 'dark';

  const { rows, links, range, anyDated } = useMemo(() => {
    const live = tasks.filter((t) => t.status !== 'cancelled' && (t.start_date || t.end_date));
    // A pinned "Today" row draws the full-height today line (the line itself is styled on its bar).
    const now = new Date(); now.setHours(0, 0, 0, 0);
    const rows: ITask[] = [{ id: 'today', text: `Today — ${now.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`, start: now, end: now, type: 'milestone', today: true }];
    for (const p of projects) {
      const mine = live.filter((t) => t.project === p.id);
      if (!mine.length) {
        // An empty swimlane keeps every workstream visible before it has dates.
        rows.push({ id: `p:${p.id}`, text: `${p.title} — unscheduled`, type: 'task', start: new Date(), end: new Date(), lane: true, color: p.color });
        continue;
      }
      rows.push({ id: `p:${p.id}`, text: p.title, type: 'summary', open: true, color: p.color });
      const ids = new Set(mine.map((t) => t.id));
      for (const t of mine.sort((a, b) => (a.start_date ?? a.end_date ?? '').localeCompare(b.start_date ?? b.end_date ?? ''))) {
        const start = toDate(t.start_date ?? t.end_date!);
        const ms = t.kind === 'milestone';
        rows.push({
          id: t.id, text: t.title, start, end: ms ? start : addDays(toDate(t.end_date ?? t.start_date!), 1), type: ms ? 'milestone' : 'task',
          parent: t.parent_id && ids.has(t.parent_id) ? t.parent_id : `p:${p.id}`, progress: t.status === 'done' ? 100 : 0,
          basis: t.date_basis, status: t.status, color: p.color, agent: recentlyByAgent(t) ? t.last_updated_by_agent!.reason : '',
        });
      }
    }
    const drawn = new Set(rows.map((r) => r.id));
    const links = live.flatMap((t) => t.dependencies.filter((d) => drawn.has(d) && drawn.has(t.id)).map((d) => ({ id: `${d}>${t.id}`, source: d, target: t.id, type: 'e2s' as const })));
    const dates = live.flatMap((t) => [t.start_date, t.end_date]).filter(Boolean) as string[];
    const first = dates.length ? toDate(dates.sort()[0]) : new Date();
    const last = dates.length ? toDate(dates.sort().at(-1)!) : new Date();
    const range = { start: addDays(first < now ? first : now, -14), end: addDays(last > addDays(now, 150) ? last : addDays(now, 150), 21) };
    return { rows, links, range, anyDated: live.length > 0 };
  }, [tasks, projects]);

  const init = (api: IApi) => {
    api.intercept('show-editor', ({ id }) => { if (!String(id).startsWith('p:') && id !== 'today') open(String(id)); return false; });
    api.intercept('add-task', () => false);
    api.intercept('delete-task', () => false);
    api.intercept('add-link', () => false);
    api.intercept('drag-task', ({ id }) => !String(id).startsWith('p:') && id !== 'today');
    api.intercept('select-task', ({ id }) => !String(id).startsWith('p:'));
    api.on('update-task', ({ id, task, inProgress }) => {
      if (inProgress || String(id).startsWith('p:') || id === 'today' || !task.start) return;
      const t = api.getTask(id);
      const body = t.type === 'milestone' ? { new_end_date: toIso(task.start) } : { new_start_date: toIso(task.start), new_end_date: toIso(addDays(task.end ?? t.end!, -1)) };
      // Moving a bar carries everything that depends on it.
      save.mutate({ path: `/tasks/${id}/shift`, body });
    });
  };

  const Theme = dark ? WillowDark : Willow;
  const z = zooms[zoom];
  return (
    <div className="gantt-card">
      <div className="gantt-bar">
        <div className="legend">
          <span className="lg lg-confirmed">Confirmed</span><span className="lg lg-target">Target</span><span className="lg lg-estimate">Estimate</span>
          <span className="lg lg-agent">✦ Moved by Ops</span><span className="lg lg-today">Today · {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
        </div>
        <span className="spacer" />
        <div className="seg">{(Object.keys(zooms) as Zoom[]).map((k) => <button key={k} className={zoom === k ? 'on' : ''} onClick={() => setZoom(k)}>{k[0].toUpperCase() + k.slice(1)}</button>)}</div>
      </div>
      <div className="gantt-wrap" style={{ height: Math.min(height, 74 + Math.max(rows.length, 4) * 32) }}>
        <Theme fonts={false}>
          <SvarGantt key={zoom} init={init} tasks={rows} links={links} scales={z.scales as any} cellWidth={z.cellWidth} cellHeight={32}
            start={range.start} end={range.end}
            columns={compact ? [{ id: 'text', header: 'Work', flexgrow: 1 }] : [{ id: 'text', header: 'Work', flexgrow: 1 }]} gridWidth={compact ? 240 : 320}
            taskTemplate={Bar} highlightTime={(d, unit) => (unit === 'day' && toIso(d) === toIso(new Date()) ? 'wx-today' : '')} />
        </Theme>
      </div>
      {!anyDated && <div className="gantt-empty">Nothing is dated yet. {emptyAction}</div>}
    </div>
  );
}

function Bar({ data }: { data: ITask }) {
  if (data.type === 'summary') return <div className="tb tb-summary" style={{ '--c': data.color } as React.CSSProperties} />;
  if (data.lane) return <div className="tb tb-lane" />;
  if (data.today) return <div className="tb tb-today" aria-label="Today" />;
  const why = data.agent ? `Moved by Ops: ${data.agent}` : `${basisLabel[data.basis as keyof typeof basisLabel]}`;
  return (
    <div className={`tb tb-${data.basis} tb-${data.status}${data.agent ? ' tb-agent' : ''}`} style={{ '--c': data.color } as React.CSSProperties} title={`${data.text}\n${why}`}>
      {data.type !== 'milestone' && <span className="tb-text">{data.text}</span>}
      {data.agent && <span className="tb-badge" aria-label="Moved by Ops">✦</span>}
    </div>
  );
}
